/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { MyHubFilterId, MyHubSortId } from "./myHub.types";
import { TMyHubFilterLayout } from "../../model/types";

/** Metadata for a single My Hub content filter chip. */
export interface TMyHubFilterMeta {
  id: MyHubFilterId;
  label: string;
  layout: TMyHubFilterLayout;
}

/** Metadata for a single My Hub sort order chip. */
export interface TMyHubSortMeta {
  id: MyHubSortId;
}

/**
 * True when the service laid this tab out as the flat vertical card list rather
 * than as carousel rows — today Saved (Figma node 115:780) and Deleted (node
 * 71:2130), because `layout: "list"` is what it sends for them.
 *
 * The two designs are the same surface: a summary line, an optional tab-level
 * bulk button, then one landscape card per recording. Everything that forks on
 * "flat list or carousels?" — the fetch, the D-pad bounds, the scroll carets —
 * asks here, and the answer is the service's alone: a tab it sends as
 * `carousel` renders as carousels, whatever the client once knew that tab as.
 *
 * The strip is required, not an optional hint: the answer lives only in what
 * the service published. A filter absent from it cannot be the active tab —
 * `applyMyHubFilter` picks the active one out of the strip, and both the mount
 * default and `resetMyHubState` leave it on a carousel tab.
 *
 * @param filter - The active My Hub filter.
 * @param filters - The filter/tab strip as the service published it.
 * @returns Whether the service laid that tab out as the flat list.
 */
export const isMyHubFlatListFilter = (
  filter: MyHubFilterId,
  filters: readonly TMyHubFilterMeta[]
): boolean => {
  const meta: TMyHubFilterMeta | undefined = filters.find(
    (item: TMyHubFilterMeta): boolean => item.id === filter
  );

  return meta !== undefined && meta.layout === TMyHubFilterLayout.LIST;
};

/** Every layout value the service is known to send. */
const MY_HUB_FILTER_LAYOUTS: string[] = Object.values(TMyHubFilterLayout);

/**
 * Narrows the `layout` a filter/tab arrived with onto {@link TMyHubFilterLayout}.
 *
 * Only `"list"` produces the flat list, so anything else — a layout added
 * service-side, or the field missing on a build older than 421 — resolves to
 * `CAROUSEL` and renders on the carousel surface.
 *
 * @param layout - The `layout` field as the service sent it.
 * @returns The matching enum value, or `CAROUSEL` when there is none.
 */
export const toMyHubFilterLayout = (
  layout: string | undefined
): TMyHubFilterLayout => {
  if (layout === undefined || !MY_HUB_FILTER_LAYOUTS.includes(layout)) {
    return TMyHubFilterLayout.CAROUSEL;
  }
  return layout as TMyHubFilterLayout;
};

/**
 * Ordered list of My Hub sort order chip options.
 *
 * Drives sort chip rendering and D-pad navigation bounds in the sort row.
 *
 * Translated to the API's `orderBy` values (`byDate` / `byName`) via
 * MY_HUB_ORDER_BY — these ids are client-side labels, not wire values.
 */
export const MY_HUB_SORT_OPTIONS: readonly TMyHubSortMeta[] = [
  { id: MyHubSortId.RECENT },
  { id: MyHubSortId.ALPHABETICAL }
];
